import { useEffect, useState } from "react";

import { ApiError, fetchWorkspaceRequest, type WorkspaceRequestStatus } from "./api.js";

/**
 * What a user with no workspace is told — the first screen in this shell that
 * shows data the API actually returned.
 *
 * `ACC-004-AC1`: a pending user can sign in and is told they are waiting.
 * `ACC-004-AC2`: a declined user is told they were declined, and why. Neither
 * can come from the token — the trigger that mints it does no I/O and so cannot
 * tell pending from declined. It has to be this request.
 */

const describe = (
  status: WorkspaceRequestStatus,
): { heading: string; detail: string } => {
  switch (status.state) {
    case "pending":
      return {
        heading: "Waiting for approval",
        detail:
          "Your workspace request has been received. An administrator has to " +
          "approve it before you can start a build.",
      };
    case "declined":
      return {
        heading: "Request declined",
        detail: status.reason ?? "No reason was recorded.",
      };
    case "approved":
      // Approved but the token still carries no tenant: the claim is written
      // at sign-in, so the session predates the approval. Saying so beats
      // showing an empty workspace.
      return {
        heading: "Approved — sign in again",
        detail:
          "Your request was approved after this session started. Your tenant " +
          "is written into the token when you sign in, so signing out and " +
          "back in will pick it up.",
      };
    case "none":
      return {
        heading: "No workspace request",
        detail:
          "You are signed in but no request exists for this account. That " +
          "happens when a user is created directly in the console rather " +
          "than through signup.",
      };
  }
};

export const WorkspaceStatus = (): React.JSX.Element => {
  const [status, setStatus] = useState<WorkspaceRequestStatus | undefined>(
    undefined,
  );
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    let live = true;
    fetchWorkspaceRequest()
      .then((result) => {
        if (live) setStatus(result);
      })
      .catch((caught: unknown) => {
        if (!live) return;
        setError(
          caught instanceof ApiError
            ? caught.message
            : "Could not reach the API.",
        );
      });
    // React 19 StrictMode mounts effects twice in development. Without this
    // the second unmount would land a stale response on an unmounted tree.
    return () => {
      live = false;
    };
  }, []);

  if (error !== undefined) {
    return (
      <section className="panel">
        <h2>Something went wrong</h2>
        <p className="blocked" role="alert">
          {error}
        </p>
      </section>
    );
  }

  if (status === undefined) {
    return (
      <section className="panel" aria-busy="true">
        <p>Checking your workspace…</p>
      </section>
    );
  }

  const { heading, detail } = describe(status);

  return (
    <section className="panel" aria-live="polite">
      <h2>{heading}</h2>
      <p>{detail}</p>
      {status.requestedAt !== undefined && (
        <p className="notice">
          Requested {new Date(status.requestedAt).toLocaleString()}
        </p>
      )}
    </section>
  );
};
